import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stopService } from '../../application/StopService';

import { Stop } from '../../domain/stop/Stop';
import { CreatePickupData } from '../../repositories/stop/StopRepository';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UseCreateStopPickupProps = ServiceResponse<(...args: any) => void, void>;

// Hooks act as 'Adapter' layer.
export const useCreateStopPickup = ({ stop }: { stop: Stop }): UseCreateStopPickupProps => {
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async ({ pickupData }: { pickupData: CreatePickupData }): Promise<Stop> =>
      stopService.createStopPickup({ stop, pickupData, project: activeProject! }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`planner_stop`, stop.id]);
        await queryClient.invalidateQueries([`planner_active_plan_stops`]);
        await queryClient.invalidateQueries([`planner_active_plan_drivers`]);
      },
      onError: (err: any) => {
        console.info(`Create Stop Pickup Error: `, err?.response);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ pickupData }: { pickupData: CreatePickupData }) => mutate({ pickupData }),
  };
};
